import 'react';
import {
    Box,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C1_CONTENT, buildC1S31Text, buildC1S9Text, isC1AutoS31Text, isC1AutoS9Text, resolveC1TeamPerformers} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {yellowFieldSx} from '@/pages/components/audit-program/a-section/yellowFieldSx';
import ProgramDateField from '@/pages/components/audit-program/ProgramDateField';

export interface C1MemorandumViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    /** Template copy; defaults to code constant when omitted. */
    content?: typeof C1_CONTENT;
}

const YES_NO = ['', 'YES', 'NO'];
const RISK_LEVELS = ['Low', 'Medium', 'High'];

function YellowInput({
    value,
    onChange,
    multiline,
    minRows,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    minRows?: number;
    placeholder?: string;
}) {
    return (
        <Box sx={yellowFieldSx}>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                multiline={multiline}
                minRows={minRows}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </Box>
    );
}

function YesNoSelect({value, onChange}: {value?: string; onChange: (v: string) => void}) {
    return (
        <InputField
            select
            compact
            align="left"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{minWidth: 100}}
        >
            {YES_NO.map((opt) => (
                <MenuItem key={opt || 'blank'} value={opt}>{opt || '—'}</MenuItem>
            ))}
        </InputField>
    );
}

/** Excel stores "C2 Completed - YES / NO"; UI shows prefix + dropdown like B1/A1. */
function completedPrefix(label?: string): string {
    if (!label) return '';
    const trimmed = label.replace(/\s*YES\s*\/\s*NO\s*$/i, '').trim();
    return trimmed.endsWith('-') ? trimmed : `${trimmed} -`;
}

/** Name above label + Date above Date — same pattern as A1 partner block. */
function SignatureBlock({
    nameValue,
    dateValue,
    nameLabel,
    onNameChange,
    onDateChange,
}: {
    nameValue: string;
    dateValue: string;
    nameLabel: string;
    onNameChange: (v: string) => void;
    onDateChange: (v: string) => void;
}) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', sm: '1fr 160px'},
                columnGap: 4,
                rowGap: 0.5,
                mb: 3,
                alignItems: 'start',
            }}
        >
            <Box>
                <YellowInput value={nameValue} onChange={onNameChange}/>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', mt: 0.25, lineHeight: 1.25}}
                >
                    {nameLabel}
                </Typography>
            </Box>
            <ProgramDateField value={dateValue} onChange={onDateChange}/>
        </Box>
    );
}

/**
 * Excel C1 Planning Memorandum.
 * Partner name defaults from header.partnerName (auditor from audit certificate), same as A1.
 */
export default function C1MemorandumView({header, values, onChange, content}: C1MemorandumViewProps) {
    const copy = (() => {
        if (!content) return C1_CONTENT;
        const pick = <K extends keyof typeof C1_CONTENT>(key: K): (typeof C1_CONTENT)[K] => {
            const v = content[key];
            if (typeof v === 'string') {
                return (v.trim() ? v : C1_CONTENT[key]) as (typeof C1_CONTENT)[K];
            }
            if (key === 'followUpSections' && Array.isArray(v) && v.length > 0) {
                const defaults = C1_CONTENT.followUpSections;
                const byKey = new Map<string, Record<string, unknown>>();
                for (const sec of defaults) {
                    byKey.set(sec.key, {...sec});
                }
                for (const sec of v as Array<Record<string, unknown>>) {
                    const keyId = String(sec.key ?? '').trim();
                    if (!keyId) continue;
                    const fallback = byKey.get(keyId) ?? {};
                    byKey.set(keyId, {
                        ...fallback,
                        ...sec,
                        key: keyId,
                        completedLabel:
                            (typeof sec.completedLabel === 'string' && sec.completedLabel.trim())
                                ? sec.completedLabel
                                : fallback.completedLabel,
                        defaultText:
                            (typeof sec.defaultText === 'string' && sec.defaultText.trim())
                                ? sec.defaultText
                                : fallback.defaultText,
                        showBody: 'showBody' in sec ? sec.showBody : fallback.showBody,
                        riskPrompt: 'riskPrompt' in sec ? sec.riskPrompt : fallback.riskPrompt,
                        riskNote: 'riskNote' in sec ? sec.riskNote : fallback.riskNote,
                    });
                }
                // Keep code order; append any unknown template-only keys at the end.
                const ordered: Record<string, unknown>[] = [];
                const seen = new Set<string>();
                for (const sec of defaults) {
                    ordered.push(byKey.get(sec.key)!);
                    seen.add(sec.key);
                }
                for (const [keyId, sec] of byKey) {
                    if (!seen.has(keyId)) ordered.push(sec);
                }
                return ordered as unknown as (typeof C1_CONTENT)[K];
            }
            if (Array.isArray(v) && v.length > 0) {
                return v as (typeof C1_CONTENT)[K];
            }
            return C1_CONTENT[key];
        };
        return {
            ...C1_CONTENT,
            ...content,
            title: pick('title'),
            intro: pick('intro'),
            section1Title: pick('section1Title'),
            confirmIntro: pick('confirmIntro'),
            approveItems: pick('approveItems'),
            signOffByLabel: pick('signOffByLabel'),
            partnerLabel: pick('partnerLabel'),
            secondPartnerLabel: pick('secondPartnerLabel'),
            section2Title: pick('section2Title'),
            completeItems: pick('completeItems'),
            teamTitle: pick('teamTitle'),
            teamIntro: pick('teamIntro'),
            teamRoles: pick('teamRoles'),
            followUpSections: pick('followUpSections'),
        };
    })();
    const partnerName = values.partnerName ?? header.partnerName ?? '';

    const syncTeamNamesIntoFollowUps = (roleKey: string, name: string) => {
        const nameKey = `team.${roleKey}.name`;
        onChange(nameKey, name);
        const next = {...values, [nameKey]: name};
        const {senior, manager} = resolveC1TeamPerformers(next);
        if (isC1AutoS31Text(values.s31)) {
            onChange('s31', buildC1S31Text(senior, manager));
        }
        if (isC1AutoS9Text(values.s9)) {
            onChange('s9', buildC1S9Text(senior, manager));
        }
    };

    return (
        <Box sx={{p: 2, maxWidth: 960}}>
            <ProgramSectionHeader header={header} variant="a1" refCode="C1" values={values} onChange={onChange}/>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.5, mb: 1.5, mt: 1}}
            >
                {copy.title}
            </Typography>

            <Typography variant="body2" sx={{mb: 2, lineHeight: 1.5, whiteSpace: 'pre-line'}}>
                {copy.intro}
            </Typography>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.section1Title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1}}>
                {copy.confirmIntro}
            </Typography>
            <Stack spacing={0.75} sx={{mb: 2, pl: 0.5}}>
                {copy.approveItems.map((item) => (
                    <Stack
                        key={`${item.num}-${item.text.slice(0, 12)}`}
                        direction="row"
                        spacing={1.5}
                        alignItems="flex-start"
                    >
                        <Typography
                            variant="body2"
                            sx={{minWidth: item.kind === 'sub' ? 28 : 22, fontWeight: 600, pl: item.kind === 'sub' ? 1 : 0}}
                        >
                            {item.num}
                        </Typography>
                        <Typography variant="body2" sx={{lineHeight: 1.45}}>{item.text}</Typography>
                    </Stack>
                ))}
            </Stack>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.signOffByLabel}
            </Typography>

            <SignatureBlock
                nameValue={partnerName}
                dateValue={values.partnerDate ?? ''}
                nameLabel={copy.partnerLabel}
                onNameChange={(v) => onChange('partnerName', v)}
                onDateChange={(v) => onChange('partnerDate', v)}
            />
            <SignatureBlock
                nameValue={values.secondPartnerName ?? ''}
                dateValue={values.secondPartnerDate ?? ''}
                nameLabel={copy.secondPartnerLabel}
                onNameChange={(v) => onChange('secondPartnerName', v)}
                onDateChange={(v) => onChange('secondPartnerDate', v)}
            />

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.section2Title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1}}>
                {copy.confirmIntro}
            </Typography>
            <Stack spacing={0.75} sx={{mb: 2, pl: 0.5}}>
                {copy.completeItems.map((item) => (
                    <Stack key={item.num} direction="row" spacing={1.5} alignItems="flex-start">
                        <Typography variant="body2" sx={{minWidth: 22, fontWeight: 600}}>{item.num}</Typography>
                        <Typography variant="body2" sx={{lineHeight: 1.45}}>{item.text}</Typography>
                    </Stack>
                ))}
            </Stack>

            <SignatureBlock
                nameValue={values.completionPartnerName ?? partnerName}
                dateValue={values.completionPartnerDate ?? ''}
                nameLabel={copy.partnerLabel}
                onNameChange={(v) => onChange('completionPartnerName', v)}
                onDateChange={(v) => onChange('completionPartnerDate', v)}
            />
            <SignatureBlock
                nameValue={values.completionSecondPartnerName ?? ''}
                dateValue={values.completionSecondPartnerDate ?? ''}
                nameLabel={copy.secondPartnerLabel}
                onNameChange={(v) => onChange('completionSecondPartnerName', v)}
                onDateChange={(v) => onChange('completionSecondPartnerDate', v)}
            />

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.teamTitle}
            </Typography>
            <Typography variant="body2" sx={{mb: 1.5}}>
                {copy.teamIntro}
            </Typography>

            <TableContainer sx={{overflowX: 'auto', mb: 2}}>
                <Table
                    size="small"
                    sx={{
                        minWidth: 720,
                        tableLayout: 'fixed',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        '& .MuiTableCell-root': {
                            px: 1.5,
                            py: 1,
                            verticalAlign: 'middle',
                        },
                    }}
                >
                    <colgroup>
                        <col style={{width: '28%'}}/>
                        <col style={{width: '32%'}}/>
                        <col style={{width: '18%'}}/>
                        <col style={{width: '22%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: 600, fontSize: '0.75rem'}}>Role</TableCell>
                            <TableCell sx={{fontWeight: 600, fontSize: '0.75rem'}}>Name</TableCell>
                            <TableCell sx={{fontWeight: 600, fontSize: '0.75rem'}} align="center">Initials</TableCell>
                            <TableCell sx={{fontWeight: 600, fontSize: '0.75rem'}} align="center">Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {copy.teamRoles.map((role) => {
                            const nameKey = `team.${role.key}.name`;
                            const defaultName = role.key === 'partner' ? partnerName : '';
                            return (
                                <TableRow key={role.key}>
                                    <TableCell>
                                        <Typography variant="body2">{role.label}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align="left"
                                            value={values[nameKey] ?? defaultName}
                                            onChange={(e) => {
                                                if (role.key === 'senior' || role.key === 'manager') {
                                                    syncTeamNamesIntoFollowUps(role.key, e.target.value);
                                                } else {
                                                    onChange(nameKey, e.target.value);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align="center"
                                            value={values[`team.${role.key}.initials`] ?? ''}
                                            onChange={(e) => onChange(`team.${role.key}.initials`, e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align="center"
                                            placeholder="dd/mm/yyyy"
                                            value={values[`team.${role.key}.date`] ?? ''}
                                            onChange={(e) => onChange(`team.${role.key}.date`, e.target.value)}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {copy.followUpSections.map((section) => {
                const prefix = completedPrefix(
                    'completedLabel' in section ? section.completedLabel : undefined,
                );
                const completedKey = `${section.key}Completed`;
                const showBody = !('showBody' in section && section.showBody === false);
                const riskPrompt = 'riskPrompt' in section ? section.riskPrompt : undefined;
                return (
                    <Box key={section.key} sx={{mb: 3}}>
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            justifyContent="space-between"
                            alignItems={{xs: 'flex-start', sm: 'center'}}
                            spacing={1}
                            sx={{mb: 1}}
                        >
                            <Typography variant="body2" fontWeight={700}>
                                {section.title}
                            </Typography>
                            {prefix ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                                        {prefix}
                                    </Typography>
                                    <YesNoSelect
                                        value={values[completedKey]}
                                        onChange={(v) => onChange(completedKey, v)}
                                    />
                                </Stack>
                            ) : null}
                        </Stack>
                        {showBody ? (
                            <YellowInput
                                value={values[section.key] ?? ''}
                                onChange={(v) => onChange(section.key, v)}
                                multiline
                                minRows={2}
                                placeholder="N/A"
                            />
                        ) : null}
                        {riskPrompt ? (
                            <Stack spacing={0.75} sx={{mt: showBody ? 1.5 : 0}}>
                                <Stack
                                    direction={{xs: 'column', sm: 'row'}}
                                    spacing={1}
                                    alignItems={{xs: 'flex-start', sm: 'center'}}
                                >
                                    <Typography variant="body2" sx={{lineHeight: 1.45}}>
                                        {riskPrompt}
                                    </Typography>
                                    <InputField
                                        select
                                        compact
                                        align="left"
                                        value={values.s81Risk ?? ''}
                                        onChange={(e) => onChange('s81Risk', e.target.value)}
                                        sx={{minWidth: 120}}
                                    >
                                        {RISK_LEVELS.map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </InputField>
                                </Stack>
                                {'riskNote' in section && section.riskNote ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {section.riskNote}
                                    </Typography>
                                ) : null}
                            </Stack>
                        ) : null}
                    </Box>
                );
            })}
        </Box>
    );
}
